"use client";

// ============================================================
// ANSICHTSSACHE-AGENT – Namens-Hinweis am Kuerzel-Kreis
// ============================================================
// Zwei Personen mit denselben Initialen sind im Kreis nicht zu
// unterscheiden. Bis zum 07.10.2026 stand der Name nur im `title`
// — der erscheint erst nach rund einer Sekunde und gar nicht, wo
// eine Zeilenflaeche ueber dem Kreis liegt. Der Hinweis hier kommt
// nach 120 ms und haengt am Rumpf:
//
//   AM RUMPF, `fixed`. Kein `overflow` einer Karte, Tabelle oder
//   eines Seitenblatts schneidet ihn ab, und er liegt ueber Dialog
//   und Blatt (`z-50`).
//
//   KEIN ZEIGER. Die Blase ist `pointer-events-none`: sie zeigt nur
//   und steht nie einem Klick im Weg — auch nicht unter einem
//   Seitenblatt, das den Rumpf fuer Zeiger sperrt.
//
//   NUR MAUS UND STIFT. Ein Tippen auf dem Telefon loest
//   `pointerenter` aus und gleich darauf `pointerleave` — die Blase
//   blitzte nur auf. Auf Beruehrung bleibt es beim Kreis.
//
// Wer einen eigenen Kreis zeichnet (statt `<Avatar>`), haengt
// `ausloeser` an das Element und rendert `blase` daneben.
// ============================================================

import { useCallback, useEffect, useRef, useState, type PointerEvent } from "react";
import { createPortal } from "react-dom";

/** Wartezeit bis zur Blase — kurz genug fuer „schnell“, lang genug gegen Flackern beim Ueberfahren. */
const VERZOEGERUNG_MS = 120;
/** Abstand der Blase zum Kreis und zum Fensterrand. */
const ABSTAND = 6;
const RAND = 8;

type Lage = { mitte: number; oben: number; unten: number };

export function useNamensHinweis(text: string | null | undefined) {
  const [lage, setLage] = useState<Lage | null>(null);
  const uhr = useRef<ReturnType<typeof setTimeout> | null>(null);

  const anhalten = useCallback(() => {
    if (uhr.current) clearTimeout(uhr.current);
    uhr.current = null;
  }, []);

  const schliessen = useCallback(() => {
    anhalten();
    setLage(null);
  }, [anhalten]);

  // Beim Rollen wandert der Kreis weg, die Blase stuende allein.
  useEffect(() => {
    if (!lage) return;
    window.addEventListener("scroll", schliessen, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", schliessen, { capture: true });
  }, [lage, schliessen]);

  useEffect(() => anhalten, [anhalten]);

  const onPointerEnter = (e: PointerEvent<Element>) => {
    if (!text || e.pointerType === "touch") return;
    const ziel = e.currentTarget;
    anhalten();
    uhr.current = setTimeout(() => {
      const r = ziel.getBoundingClientRect();
      setLage({ mitte: r.left + r.width / 2, oben: r.top, unten: r.bottom });
    }, VERZOEGERUNG_MS);
  };

  // Die Blase wird erst gemessen, wenn sie steht: oben, wenn Platz
  // ist, sonst unter dem Kreis; seitlich nie ueber den Fensterrand.
  const ausrichten = useCallback(
    (blase: HTMLSpanElement | null) => {
      if (!blase || !lage) return;
      const b = blase.getBoundingClientRect();
      const links = Math.min(
        Math.max(lage.mitte - b.width / 2, RAND),
        window.innerWidth - b.width - RAND,
      );
      const oben =
        lage.oben - b.height - ABSTAND >= RAND
          ? lage.oben - b.height - ABSTAND
          : lage.unten + ABSTAND;
      blase.style.left = `${links}px`;
      blase.style.top = `${oben}px`;
      blase.style.visibility = "visible";
    },
    [lage],
  );

  const blase =
    lage && text
      ? createPortal(
          <span
            ref={ausrichten}
            role="tooltip"
            className="text-caption text-text-inverse bg-bg-inverse shadow-dropdown animate-fade-in pointer-events-none fixed z-[200] rounded px-2 py-1 whitespace-nowrap"
            style={{ left: 0, top: 0, visibility: "hidden" }}
          >
            {text}
          </span>,
          document.body,
        )
      : null;

  return {
    ausloeser: { onPointerEnter, onPointerLeave: schliessen, onPointerDown: schliessen },
    blase,
  };
}
